import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ResultsTable from '../components/ResultsTable';
import {
  runScreening,
  verifySession,
  ScreeningResult,
} from '../api/screening.api';
import {
  loadCheckoutStash,
  clearCheckoutStash,
} from '../api/checkoutStash';

type ProcessState = 'verifying' | 'screening' | 'results' | 'error';

/**
 * Return-from-Stripe page. Reached via /process?session_id=cs_xxx after
 * Stripe Checkout completes. Reads sessionStorage stash, verifies payment
 * server-side, runs screening, renders results.
 *
 * The flow is auto-driven (no user clicks needed) because the user has
 * already done the work — they uploaded, they paid, now they just want
 * to see results land.
 */
export default function ProcessPage() {
  const [state, setState] = useState<ProcessState>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScreeningResult | null>(null);
  const [statusMsg, setStatusMsg] = useState('Verifying payment...');

  // Guard against React 18 strict-mode double-effect: the screening call
  // is non-idempotent (it consumes the session). useRef ensures it runs once.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    (async () => {
      // 1. Pull session_id from URL (Stripe redirect) and from stash
      //    (our own bookmark). They should match.
      const params = new URLSearchParams(window.location.search);
      const urlSessionId = params.get('session_id');
      const stash = loadCheckoutStash();

      if (!urlSessionId) {
        setError('No session_id in URL. Did you arrive here from checkout?');
        setState('error');
        return;
      }
      if (!stash) {
        setError(
          'No upload data found. The session may have expired or you opened this page in a new tab. Please re-upload your files.',
        );
        setState('error');
        return;
      }
      if (stash.sessionId !== urlSessionId) {
        setError(
          'Session mismatch. Please re-upload to start a fresh checkout.',
        );
        setState('error');
        return;
      }

      // 2. Verify payment server-side.
      try {
        const verification = await verifySession(urlSessionId);
        if (!verification.paid) {
          setError(
            'Payment was not completed. If you cancelled, please re-upload to try again.',
          );
          setState('error');
          return;
        }
        if (verification.consumed) {
          setError(
            'This session has already been used. Each payment is one screening run.',
          );
          setState('error');
          return;
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message || err.message || 'Verification failed',
        );
        setState('error');
        return;
      }

      // 3. Run screening with the verified session.
      setState('screening');
      setStatusMsg(
        `Screening ${stash.uploadData.resumes.length} resumes — this takes 30-60 seconds per resume...`,
      );

      try {
        const screeningResult = await runScreening(
          urlSessionId,
          stash.uploadData.jdText || '',
          stash.uploadData.resumesFull,
          stash.allEvaluations.length > 0 ? stash.allEvaluations : undefined,
          stash.customInstructions || undefined,
        );
        setResult(screeningResult);
        setState('results');
        // Clear stash now that we're done — protects against accidental
        // re-screening if user refreshes (session is also marked 'consumed'
        // server-side, so a refresh would 409 anyway).
        clearCheckoutStash();
      } catch (err: any) {
        setError(
          err.response?.data?.message || err.message || 'Screening failed',
        );
        setState('error');
      }
    })();
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          StrixScreener
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
          Processing
        </Typography>
      </Box>

      {(state === 'verifying' || state === 'screening') && (
        <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {state === 'verifying' ? 'Verifying payment…' : 'Screening in progress…'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusMsg}
          </Typography>
        </Paper>
      )}

      {state === 'error' && error && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            href="/"
          >
            Back to Upload
          </Button>
        </>
      )}

      {state === 'results' && result && (
        <Box sx={{ mt: 3 }}>
          <ResultsTable result={result} />
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              href="/"
            >
              New Screening
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}
